import { Injectable } from "@angular/core";
import { RunView, Metadata, LogError } from "@memberjunction/core";
import { BehaviorSubject } from "rxjs";
import { PermissionsEntity, ResourcesEntity, RoleResourcePermissionEntity } from 'mj_generatedentities';
import { EntityNames } from './constants';
import { DataFetchService } from '../services/data-fetch.service';
import { RoleEntity } from "@memberjunction/core-entities";

@Injectable({
    providedIn: 'root'
})
export class SharedData {
    [key: string]: any; // Index signature

    private static _instance: SharedData;

    private setupCompleteSource = new BehaviorSubject<boolean>(false);
    setupComplete$ = this.setupCompleteSource.asObservable();

    // Cache for permission-related data
    private permissionsCache: PermissionsEntity[] = [];
    private resourcesCache: ResourcesEntity[] = [];
    private roleResourcePermissionsCache: RoleResourcePermissionEntity[] = [];
    private rolesCache: RoleEntity[] = [];
    private permissionDataLoaded: boolean = false;

    // Runtime role data from GET ROLE API
    private currentUserRoleName: string | null = null;
    private currentUserRoleId: string | null = null;

    constructor(private dataFetchService: DataFetchService) {
        if (SharedData._instance) {
            return SharedData._instance;
        }
        else {
            // first instance
            SharedData._instance = this;
            return this;// not needed but for clarity
        }
    }

    public async Refresh() {
        await this.LoadData();
        this.setupCompleteSource.next(true);
    }

    private async LoadData() {
        // Load permission-related data first
        await this.loadPermissionData();
        
        // Load other data using the original pattern
        const rv = new RunView();

        const loadStructure = await this.getLoadStructure();
        for (let i = 0; i < loadStructure.length; i++) {
            // check each dataset for updates
            const dataset = loadStructure[i];
            for (let j = 0; j < dataset.entities.length; j++) {
                const item = dataset.entities[j];
                const result = await rv.RunView({
                    EntityName: item.entityName,
                    ExtraFilter: item.whereClause
                });
                if (result && result.Success && Array.isArray(result.Results) && result.Results.length > 0) {
                    this[item.code].next(result.Results);
                }
            }
        }
    }

    private async loadPermissionData(): Promise<void> {
        try {
            // Load ALL active role permissions - don't filter by current user
            // This is needed for impersonation to work properly
            const results = await this.dataFetchService.fetchMultipleEntities([
                { EntityName: EntityNames.Permissions, ResultType: 'entity_object' },
                { EntityName: EntityNames.Resources, ResultType: 'entity_object', ExtraFilter: 'IsActive = 1' },
                { 
                    EntityName: EntityNames.RoleResourcePermissions, 
                    ResultType: 'entity_object',
                    ExtraFilter: 'IsActive = 1'
                },
                { EntityName: EntityNames.Roles, ResultType: 'entity_object' }
            ]);

            this.permissionsCache = results[0].data as PermissionsEntity[] || [];
            this.resourcesCache = results[1].data as ResourcesEntity[] || [];
            this.roleResourcePermissionsCache = results[2].data as RoleResourcePermissionEntity[] || [];
            this.rolesCache = results[3].data as RoleEntity[] || [];
            
            this.permissionDataLoaded = true;
        } catch (error) {
            LogError('Error loading permission data:', undefined, error);
            // Set empty arrays on error
            this.permissionsCache = [];
            this.resourcesCache = [];
            this.roleResourcePermissionsCache = [];
            this.permissionDataLoaded = true;
        }
    }

    // Direct access methods for permission data
    public getPermissions(): PermissionsEntity[] {
        return this.permissionsCache;
    }

    public getResources(): ResourcesEntity[] {
        return this.resourcesCache;
    }

    public getRoleResourcePermissions(): RoleResourcePermissionEntity[] {
        return this.roleResourcePermissionsCache;
    }

    public getRoles(): RoleEntity[] {
        return this.rolesCache;
    }

    public isPermissionDataLoaded(): boolean {
        return this.permissionDataLoaded;
    }

    /**
     * Set current user's runtime role from GET ROLE API
     * @param roleName - Role name returned from GET ROLE API
     */
    public setCurrentUserRole(roleName: string): void {
        this.currentUserRoleName = roleName;
        // Find role ID from cached roles
        const role = this.rolesCache.find(r => r.Name === roleName);
        this.currentUserRoleId = role?.ID || null;
    }

    /**
     * Get current user's runtime role name
     */
    public getCurrentUserRoleName(): string | null {
        return this.currentUserRoleName;
    }

    /**
     * Get current user's runtime role ID
     */
    public getCurrentUserRoleId(): string | null {
        return this.currentUserRoleId;
    }

    /**
     * Get role ID by role name from cached roles
     * @param roleName - Name of the role
     * @returns Role ID or null if not found
     */
    public getRoleIdByName(roleName: string): string | null {
        const role = this.rolesCache.find(r => r.Name === roleName);
        return role?.ID || null;
    }

    /**
     * Get role name by role ID from cached roles
     * @param roleId - ID of the role
     * @returns Role name or null if not found
     */
    public getRoleNameById(roleId: string): string | null {
        const role = this.rolesCache.find(r => r.ID === roleId);
        return role?.Name || null;
    }

    /**
     * Clear current user role data
     */
    public clearCurrentUserRole(): void {
        this.currentUserRoleName = null;
        this.currentUserRoleId = null;
    }

    private async getLoadStructure(): Promise<any[]> {
        return [];
    }

    /*
    Use the format below to define and load data from the database on startup

    private entitySubject = new BehaviorSubject<>([]);
    protected entity$ = this.entitySubject.asObservable();
    public get GetEntity(): Observable<[]> {
        return this.entity$;
    }

    private async getLoadStructure(): Promise<any[]> {
        return [
            {
                datasetName: 'Dataset Name',
                entities: [                
                    { 
                        entityName: 'EntityName', 
                        code: 'entitySubject',
                        whereClause: 'extra filter if needed' 
                    }
                ]   
            }
        ]
    }

    */

}